import React, { useState } from 'react';
import {
  PaperAirplaneIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sent';
  recipientCount: number;
  sentAt?: string;
  scheduledFor?: string;
  openRate?: number;
  clickRate?: number;
}

const CreatorEmailCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([
    {
      id: '1',
      name: 'Monthly Update - January',
      subject: 'Exciting updates for this month!',
      status: 'sent',
      recipientCount: 1234,
      sentAt: '2024-01-15',
      openRate: 68.5,
      clickRate: 24.3,
    },
    {
      id: '2',
      name: 'Special Announcement',
      subject: 'Big news coming your way',
      status: 'scheduled',
      recipientCount: 1234,
      scheduledFor: '2024-02-01',
    },
    {
      id: '3',
      name: 'Welcome Series - Part 1',
      subject: 'Welcome to my Patreon!',
      status: 'draft',
      recipientCount: 0,
    },
  ]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    subject: '',
    body: '',
    tierFilter: 'all',
    scheduleDate: '',
  });

  const handleCreateCampaign = () => {
    const newCampaign: Campaign = {
      id: Date.now().toString(),
      name: createForm.name,
      subject: createForm.subject,
      status: createForm.scheduleDate ? 'scheduled' : 'draft',
      recipientCount: 1234,
      scheduledFor: createForm.scheduleDate || undefined,
    };
    
    setCampaigns([newCampaign, ...campaigns]);
    setShowCreateModal(false);
    setCreateForm({
      name: '',
      subject: '',
      body: '',
      tierFilter: 'all',
      scheduleDate: '',
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
    };
    return badges[status as keyof typeof badges];
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      draft: PencilSquareIcon,
      scheduled: ClockIcon,
      sent: CheckCircleIcon,
    };
    return icons[status as keyof typeof icons];
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="mt-2 text-sm text-gray-600">
            Create and manage email campaigns to engage with your patrons.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PaperAirplaneIcon className="h-5 w-5 mr-2" />
          New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <EnvelopeIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
              <p className="text-2xl font-semibold text-gray-900">{campaigns.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sent</p>
              <p className="text-2xl font-semibold text-gray-900">
                {campaigns.filter(c => c.status === 'sent').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Scheduled</p>
              <p className="text-2xl font-semibold text-gray-900">
                {campaigns.filter(c => c.status === 'scheduled').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <PencilSquareIcon className="h-8 w-8 text-gray-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Drafts</p>
              <p className="text-2xl font-semibold text-gray-900">
                {campaigns.filter(c => c.status === 'draft').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {campaigns.map((campaign) => {
            const StatusIcon = getStatusIcon(campaign.status);
            
            return (
              <div key={campaign.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <StatusIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Subject:</span> {campaign.subject}
                      </p>
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {campaign.recipientCount} recipients
                        </div>
                        {campaign.sentAt && (
                          <div>Sent: {new Date(campaign.sentAt).toLocaleDateString()}</div>
                        )}
                        {campaign.scheduledFor && (
                          <div>Scheduled for: {new Date(campaign.scheduledFor).toLocaleDateString()}</div>
                        )}
                      </div>
                      {campaign.openRate !== undefined && (
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">Open Rate</span>
                              <span className="font-medium text-green-600">{campaign.openRate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${campaign.openRate}%` }}
                              ></div>
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">Click Rate</span>
                              <span className="font-medium text-blue-600">{campaign.clickRate}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${campaign.clickRate}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {campaign.status === 'draft' && (
                      <>
                        <button className="px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md">
                          Edit
                        </button>
                        <button className="px-3 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md">
                          Send Now
                        </button>
                      </>
                    )}
                    {campaign.status === 'scheduled' && (
                      <button className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md">
                        Cancel
                      </button>
                    )}
                    {campaign.status === 'sent' && (
                      <button className="px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md">
                        View Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Create Email Campaign
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder="e.g., Monthly Update"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email Subject</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.subject}
                      onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
                      placeholder="e.g., Exciting updates for this month!"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email Body</label>
                    <textarea
                      rows={6}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={createForm.body}
                      onChange={(e) => setCreateForm({ ...createForm, body: e.target.value })}
                      placeholder="Write your email content here..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Recipients</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.tierFilter}
                        onChange={(e) => setCreateForm({ ...createForm, tierFilter: e.target.value })}
                      >
                        <option value="all">All Patrons</option>
                        <option value="platinum">Platinum Tier</option>
                        <option value="gold">Gold Tier</option>
                        <option value="silver">Silver Tier</option>
                        <option value="bronze">Bronze Tier</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Schedule (Optional)</label>
                      <input
                        type="datetime-local"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={createForm.scheduleDate}
                        onChange={(e) => setCreateForm({ ...createForm, scheduleDate: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-3 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                  onClick={handleCreateCampaign}
                >
                  {createForm.scheduleDate ? 'Schedule' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-3 sm:text-sm"
                  onClick={handleCreateCampaign}
                >
                  Send Now
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorEmailCampaignsPage;
